/**
 * Lab Detail Activity matrix — CM-API analytics proxies (Agent parity).
 * Agent scope: POST /v1/members/detail per period window.
 * All Members: POST /v1/agents/member-pnl summed across pages.
 */
import { useQuery } from '@tanstack/react-query'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { asAnalyticsMemberDetail } from '@/api/map/memberDetail'
import {
  ACTIVITY_PERIODS,
  activityPeriodRanges,
  emptyActivityMatrix,
  emptyPeriodFigures,
  figuresFromMemberPeriod,
  setPeriodFigures,
  type ActivityPeriod,
  type ActivityPeriodFigures,
  type MemberActivityMatrix,
} from '@/api/map/memberActivityMatrix'
import { fromMinorUnits } from '@/features/hierarchy/hierarchy'

export type MemberActivityMatrixQuery = {
  memberId: string
  memberScope: 'agent' | 'all-members'
  fromDate: string
  toDate: string
}

const PAGE_SIZE = 100
const MAX_PAGES = 50

function fromMinorUnitsSigned(minor: string | null | undefined): number {
  if (minor == null || minor === '') return 0
  const n = Number(minor)
  return Number.isFinite(n) ? n / 100 : 0
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

async function loadAgentPeriod(
  memberId: string,
  from: string,
  to: string,
): Promise<ActivityPeriodFigures> {
  const live = await cmApi.memberDetail(authHeaders(), memberId, from, to)
  const detail = asAnalyticsMemberDetail(live)
  return figuresFromMemberPeriod(detail?.period ?? undefined)
}

async function loadAllMembersPeriod(
  memberId: string,
  from: string,
  to: string,
): Promise<ActivityPeriodFigures> {
  let turnover = 0
  let pnl = 0
  for (let page = 0; page < MAX_PAGES; page++) {
    const live = await cmApi.memberPnl(authHeaders(), memberId, {
      from,
      to,
      page,
      size: PAGE_SIZE,
      sort: 'pnl',
    })
    const rows = isRecord(live) && Array.isArray(live.rows) ? live.rows : []
    for (const raw of rows) {
      if (!isRecord(raw)) continue
      turnover += fromMinorUnits(
        typeof raw.turnoverMinor === 'string' ? raw.turnoverMinor : undefined,
      )
      pnl +=
        raw.memberNetMinor !== undefined
          ? fromMinorUnitsSigned(
              typeof raw.memberNetMinor === 'string' ? raw.memberNetMinor : undefined,
            )
          : -fromMinorUnitsSigned(
              typeof raw.bookNetMinor === 'string' ? raw.bookNetMinor : undefined,
            )
    }
    const got = rows.length
    const total = isRecord(live) && typeof live.total === 'number' ? live.total : undefined
    if (got < PAGE_SIZE) break
    if (typeof total === 'number' && (page + 1) * PAGE_SIZE >= total) break
  }
  return {
    win: Math.max(0, pnl),
    betfairComm: 0,
    commission: 0,
    pnl,
    turnover,
  }
}

/** Exported for Vitest whitebox (agent vs all-members / soft-empty periods). */
export async function loadLiveMatrix(
  query: MemberActivityMatrixQuery,
): Promise<MemberActivityMatrix> {
  const base = emptyActivityMatrix(query.memberId, query.memberScope)
  const windows = activityPeriodRanges(query.fromDate, query.toDate)
  let next = base
  for (const { id } of ACTIVITY_PERIODS) {
    const { from, to } = windows[id]
    try {
      const figures =
        query.memberScope === 'all-members'
          ? await loadAllMembersPeriod(query.memberId, from, to)
          : await loadAgentPeriod(query.memberId, from, to)
      next = setPeriodFigures(next, id as ActivityPeriod, figures)
    } catch {
      next = setPeriodFigures(next, id as ActivityPeriod, emptyPeriodFigures())
    }
  }
  return next
}

export function useMemberActivityMatrix(query: MemberActivityMatrixQuery, enabled = true) {
  return useQuery({
    queryKey: ['cm', 'member-activity-matrix', query],
    queryFn: () => loadLiveMatrix(query),
    enabled: enabled && Boolean(query.memberId),
  })
}
